import { useSelector } from '@tanstack/react-form';
import { Button, Card, Form, LearnMoreLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { useCallback, useRef } from 'react';
import { useHandlers } from '../../../context/page-context';
import { useFormProps } from '../../../hooks/use-forms-props';
import { useModal } from '../../../hooks/use-modal';
import { AddressManagementCard } from '../components/builder/address-management-card';
import { AllowCheckoutCard } from '../components/builder/allow-checkout-card';
import { CreditSettingsCard } from '../components/builder/credit-settings-card';
import { CustomerGroupBuilderDetailsCard } from '../components/builder/customer-group-builder-details-card';
import { CustomerGroupBuilderSummaryCard } from '../components/builder/customer-group-builder-summary-card';
import { OrderQuantityLimitCard } from '../components/builder/order-quantity-limit-card';
import { OrderTotalLimitsCard } from '../components/builder/order-total-limits-card';
import { OverrideSettingsCard } from '../components/builder/override-settings-card';
import { PaymentMethodsCard } from '../components/builder/payment-methods-card';
import { PriceListsCard } from '../components/builder/price-lists-card';
import { QuotingEngineCard } from '../components/builder/quoting-engine-card';
import { StockDisplayCard } from '../components/builder/stock-display-card';
import { StockLocationCard } from '../components/builder/stock-location-card';
import { DeleteCustomerGroupModal } from '../components/delete-customer-group-modal';
import { SwitchToQuickSetupModal } from '../components/switch-to-quick-setup-modal';
import { CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS } from '../create-method';
import { useCustomerGroupBuilderForm } from '../hooks/use-customer-group-builder-form';
import { useCustomerGroupTag } from '../hooks/use-customer-group-tag';
import { filterSettings } from '../transformers';
import type { SettingComponentMap, SettingComponentMapKeys } from '../types/settings.types';

const SETTINGS_COMPONENTS: SettingComponentMap = {
  priceListSelection: PriceListsCard,
  allowedPaymentMethods: PaymentMethodsCard,
  quotingEngine: QuotingEngineCard,
  orderItemQuantityValidation: OrderQuantityLimitCard,
  orderTotalsValidation: OrderTotalLimitsCard,
  allowAddressEditing: AddressManagementCard,
  paymentOnAccountEnforceCreditLimit: CreditSettingsCard,
  multiLocationStockConfig: StockLocationCard,
  stockDisplay: StockDisplayCard,
  allowCheckout: AllowCheckoutCard,
} as const;

export function CustomerGroupsBuilderContent({ editSlug }: { editSlug?: string }) {
  const { navigationHandler } = useHandlers();
  const { isOpen, openModal, closeModal } = useModal();
  const {
    isOpen: isSwitchToQuickSetupModalOpen,
    openModal: openSwitchToQuickSetupModal,
    closeModal: closeSwitchToQuickSetupModal,
  } = useModal();

  const form = useCustomerGroupBuilderForm();

  const { tags: groupTags } = useCustomerGroupTag(editSlug);
  const tagLink = groupTags[groupTags.length - 1]?.link;

  const isEditing = !!editSlug;

  const componentRefs = useRef<Record<SettingComponentMapKeys, HTMLElement | null>>(
    {} as Record<SettingComponentMapKeys, HTMLElement | null>,
  );

  const scrollToSection = useCallback((key: SettingComponentMapKeys) => {
    requestAnimationFrame(() => {
      const element = componentRefs.current[key];
      if (!element) return;

      // Don't use scrollIntoView - it scrolls overflow:hidden ancestors too,
      // which pushes the layout container above the viewport with no way to scroll back.
      let scrollParent: HTMLElement | null = element.parentElement;
      while (scrollParent) {
        const { overflowY } = getComputedStyle(scrollParent);
        if (overflowY === 'auto' || overflowY === 'scroll') break;
        scrollParent = scrollParent.parentElement;
      }
      if (!scrollParent) return;

      const containerRect = scrollParent.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      scrollParent.scrollTo({
        top: scrollParent.scrollTop + elementRect.top - containerRect.top,
        behavior: 'smooth',
      });
    });
  }, []);

  const formProps = useFormProps(form);

  const customerGroupName = useSelector(form.store, (state) =>
    isEditing ? state.values.name : '',
  );

  const hasChildren = useSelector(form.store, (state) => state.values.hasChildren);

  const title =
    (isEditing ? customerGroupName : undefined) ||
    t(
      `features:customer_group.pages.customer_group_builder_page.header.title.${isEditing ? 'edit' : 'create'}`,
    );

  return (
    <Page
      title={title}
      narrow
      hasSideBar
      backUrl="/customers/groups"
      secondaryActions={[
        {
          disabled: !isEditing || !tagLink,
          label: t('features:customer_group.pages.customer_group_builder_page.more_actions.view'),
          icon: 'tag',
          onClick: () => {
            window.open(tagLink, '_blank');
          },
        },
        {
          disabled: !isEditing || editSlug === 'base' || hasChildren,
          label: t('features:customer_group.pages.customer_group_builder_page.more_actions.delete'),
          icon: 'delete',
          danger: true,
          onClick: () => {
            openModal();
            form.setErrorMap({ onSubmit: { fields: {} } });
          },
        },
        ...(isEditing
          ? []
          : [
              {
                label: t(
                  'features:customer_group.pages.customer_group_builder_page.more_actions.switch_to_quick_setup',
                ),
                icon: 'adjust' as const,
                onClick: openSwitchToQuickSetupModal,
              },
            ]),
      ]}
      description={
        <>
          {t('features:customer_group.pages.customer_group_builder_page.header.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/customers" />
        </>
      }
    >
      <Form {...formProps}>
        <CustomerGroupBuilderDetailsCard isEditing={isEditing} />
        <form.Subscribe selector={(state) => state.values.settings}>
          {(settings) => {
            const customizedSettings = filterSettings(settings).filter(
              (setting) => setting.value !== undefined,
            );

            if (customizedSettings.length === 0) {
              return null;
            }

            return (
              <Card>
                <Card.Header headingLevel="h2">
                  {t('features:customer_group.components.customized_rules_card.title')}
                </Card.Header>
                <Card.Body>
                  <div className="flex flex-col gap-4">
                    {customizedSettings.map((setting) => {
                      const Component = SETTINGS_COMPONENTS[setting.key];
                      if (Component === undefined) {
                        return undefined;
                      }
                      return (
                        <section
                          key={setting.key}
                          ref={(element) => {
                            if (element) {
                              componentRefs.current[setting.key] = element;
                            } else {
                              delete componentRefs.current[setting.key];
                            }
                          }}
                        >
                          <Component />
                        </section>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            );
          }}
        </form.Subscribe>
        <OverrideSettingsCard scrollToSection={scrollToSection} isEditing={isEditing} />
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={formProps.loading}
            onClick={() => navigationHandler.navigate('/customers/groups')}
          >
            {t('buttons:cancel')}
          </Button>
          <Button type="submit" disabled={!formProps.isDirty} loading={formProps.loading}>
            {t(
              `features:customer_group.pages.customer_group_builder_page.header.actions.${isEditing ? 'update' : 'create'}`,
            )}
          </Button>
        </div>
      </Form>
      <aside className="hidden xl:sticky xl:top-4 xl:block xl:self-start">
        <CustomerGroupBuilderSummaryCard scrollToSection={scrollToSection} />
      </aside>
      {editSlug && (
        <DeleteCustomerGroupModal
          slug={editSlug}
          name={customerGroupName}
          isOpen={isOpen}
          closeModal={closeModal}
        />
      )}
      <SwitchToQuickSetupModal
        isOpen={isSwitchToQuickSetupModalOpen}
        closeModal={closeSwitchToQuickSetupModal}
        onConfirm={() =>
          navigationHandler.navigateById(CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS.quick)
        }
      />
    </Page>
  );
}
